import React, { useContext, useEffect, useState, useRef } from "react";
import { Disclosure } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import AppContext from "../../context/AppContext";
import dummyImg from "../../assets/dummy.webp";
import { getUnreadMsgCount } from "../../service/Notification";
import { getNotificationUnreadCount } from "../../service/CallCenterNotifications";
import { getAlertSummary } from "../../service/CallCenterAlerts";
import { hasScreenAccess } from "../../utils/permissions";
import Logout from "../common/Logout";
import LangComp from "./langComp";
import OrganizationContextSelector from "../common/OrganizationContextSelector";
import DashboardMenus from "./DashboardMenus.js";
import { FiBell, FiLogOut, FiSearch, FiUser } from "react-icons/fi";
import { IoChevronDownOutline } from "react-icons/io5";
import translations from "../common/Translations.js";
import callCenterTranslations from "../common/CallCenterTranslations.js";
import { isPlatformSuperAdmin } from "../../utils/organizationContext";
import { PERMISSIONS_CHANGED_EVENT } from "../../utils/permissions";
import { isDesktopApp } from "../../utils/isDesktop";

export const MainNav = () => {
  const [showModal, setShowModal] = useState(false);
  const [userType, setUserType] = useState(null);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const context = useContext(AppContext);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showNotificationsMenu, setShowNotificationsMenu] = useState(false);
  const [agentAlertAttentionCount, setAgentAlertAttentionCount] = useState(0);
  const {
    theme,
    profilePic,
    setProfilePic,
    setPriority,
    setActive,
    setSelectedUserId,
    setSelectedUserName,
    langMode,
    totalUnreadMsgCount,
    setTotalUnreadMsgCount,
    toggleLangMode,
  } = context;
  const user = Cookies.get("userName");
  const organizationName =
    typeof localStorage !== "undefined" ? localStorage.getItem("selectedOrgName") : null;
  const resolvedUserType = userType || Cookies.get("userType");
  const [permissionsTick, setPermissionsTick] = useState(0);
  const isSuperAdmin = isPlatformSuperAdmin();
  const desktopShell = isDesktopApp();
  const orgBadgeLabel = organizationName || "EEMAD";
  const navbarRef = useRef(null);
  const profileRef = useRef(null);
  const notificationsRef = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleReset = () => {
    setActive("Status");
    setPriority("Priority");
    setSelectedUserId("");
    setSelectedUserName("Default");
  };

  useEffect(() => {
    const profilePicCookie = Cookies.get("profilePic") || dummyImg;
    setProfilePic(profilePicCookie !== "null" ? profilePicCookie : dummyImg);
    setUserType(Cookies.get("userType"));
  }, [setProfilePic, permissionsTick]);

  useEffect(() => {
    const onPermissionsChanged = () => setPermissionsTick((value) => value + 1);
    window.addEventListener(PERMISSIONS_CHANGED_EVENT, onPermissionsChanged);
    return () => window.removeEventListener(PERMISSIONS_CHANGED_EVENT, onPermissionsChanged);
  }, []);

  useEffect(() => {
    const fetchUnreadMsgCount = async () => {
      try {
        if (canAccessCcNotifications()) {
          const data = await getNotificationUnreadCount();
          setTotalUnreadMsgCount(Number(data?.unreadCount) || 0);
        } else {
          const count = await getUnreadMsgCount();
          setTotalUnreadMsgCount(count?.body?.value?.unreadMsgCount ?? 0);
        }
      } catch (error) {
        console.error("Failed to fetch unread message count:", error);
      }
    };

    const fetchAgentAlertCount = async () => {
      if (!hasScreenAccess("CC_AGENT_ALERTS", "canView")) {
        setAgentAlertAttentionCount(0);
        return;
      }
      try {
        const summary = await getAlertSummary();
        setAgentAlertAttentionCount(Number(summary?.attentionCount ?? summary?.attention ?? 0));
      } catch (error) {
        console.error("Failed to fetch agent alert count:", error);
        setAgentAlertAttentionCount(0);
      }
    };

    const refreshCount = () => {
      fetchUnreadMsgCount();
      fetchAgentAlertCount();
    };

    refreshCount();
    const intervalId = window.setInterval(refreshCount, 30000);
    window.addEventListener("focus", refreshCount);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshCount);
    };
  }, [setTotalUnreadMsgCount]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleLogout = () => {
    setShowProfileModal(false);
    setIsNavbarOpen(false);
    setShowModal(true);
  };

  const ccT = (key) =>
    callCenterTranslations[langMode]?.[key] || callCenterTranslations.en[key] || key;
  const isRtl = langMode === "ar";
  const searchPlaceholder = ccT("ccGlobalSearchPlaceholder");

  const canAccessCcTickets = () => hasScreenAccess("CC_TICKETS", "canView");

  const canAccessCcNotifications = () =>
    hasScreenAccess("CC_PAYMENT_REMINDERS", "canView") ||
    hasScreenAccess("CC_PAYMENT_CONFIRMATIONS", "canView");

  const canAccessCcAgentAlerts = () => hasScreenAccess("CC_AGENT_ALERTS", "canView");

  const canAccessCcNotificationMenu = () =>
    canAccessCcNotifications() || canAccessCcAgentAlerts();

  const notificationBadgeCount =
    Number(totalUnreadMsgCount) + Number(agentAlertAttentionCount);

  const renderNotificationMenuItems = (itemClassName) => (
    <>
      {canAccessCcNotifications() && (
        <>
          <button
            type="button"
            className={itemClassName}
            onClick={() =>
              handleOpenNotificationScreen("/call-center/notifications/reminders")
            }
          >
            <FiBell className="h-4 w-4 text-slate-400" />
            {ccT("ccMenuPaymentReminders")}
          </button>
          <button
            type="button"
            className={itemClassName}
            onClick={() =>
              handleOpenNotificationScreen("/call-center/notifications/confirmations")
            }
          >
            <FiBell className="h-4 w-4 text-slate-400" />
            {ccT("ccMenuPaymentConfirmations")}
          </button>
        </>
      )}
      {canAccessCcAgentAlerts() && (
        <button
          type="button"
          className={itemClassName}
          onClick={() => handleOpenNotificationScreen("/call-center/alerts")}
        >
          <FiBell className="h-4 w-4 text-slate-400" />
          <span className="flex flex-1 items-center justify-between gap-2">
            <span>{ccT("ccMenuAgentResponseAlerts")}</span>
            {agentAlertAttentionCount > 0 && (
              <span className="rounded-full bg-[#EF4A00] px-1.5 py-0.5 text-[10px] font-bold text-white">
                {agentAlertAttentionCount > 99 ? "99+" : agentAlertAttentionCount}
              </span>
            )}
          </span>
        </button>
      )}
    </>
  );

  const handleOpenNotificationScreen = (path) => {
    Cookies.set("notipreviousPath", location.pathname);
    navigate(path);
    handleReset();
    setShowNotificationsMenu(false);
    setIsNavbarOpen(false);
  };

  const handleToggleNotificationsMenu = () => {
    if (!canAccessCcNotificationMenu()) {
      Cookies.set("notipreviousPath", location.pathname);
      navigate("/notification");
      handleReset();
      setIsNavbarOpen(false);
      return;
    }
    setShowProfileModal(false);
    setShowNotificationsMenu((open) => !open);
  };

  const formatUserType = (role) => {
    if (!role) return "User";
    const normalized = String(role).toLowerCase();
    switch (normalized) {
      case "super_admin":
        return "Super Admin";
      case "admin":
        return "Admin";
      case "office_user":
        return "Office User";
      case "call_admin":
        return "Call admin";
      default:
        return String(role)
          .replace(/_/g, " ")
          .replace(/\b\w/g, (char) => char.toUpperCase());
    }
  };

  const openProfileMenu = () => {
    Cookies.set("previousPath", location.pathname);
    setShowProfileModal((open) => !open);
    handleReset();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsNavbarOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileModal(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotificationsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const pillButtonClass =
    "flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-slate-300 hover:bg-slate-50";

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;

    if (canAccessCcTickets()) {
      navigate(`/call-center/tickets?search=${encodeURIComponent(query)}`);
      return;
    }
    navigate(`/all-users?search=${encodeURIComponent(query)}`);
  };

  return (
    <>
      <Disclosure
        as="nav"
        ref={navbarRef}
        className={`relative z-[100] w-full shrink-0 overflow-visible border-b border-slate-200/60 bg-white ${
          theme === "dark" ? "dark-mode !border-gray-800 !bg-gray-950/90" : ""
        } ${theme === "high-contrast" ? "high-contrast border-b-2 border-black bg-white" : ""}`}
      >
        {() => (
          <>
            <div
              className={`flex h-[4.25rem] w-full items-center justify-between gap-3 px-4 sm:px-6 ${
                isRtl ? "flex-row-reverse" : ""
              }`}
            >
              <div className="flex min-w-0 max-w-2xl flex-1 items-center justify-start gap-3 lg:max-w-[42rem]">
                <div className="flex shrink-0 items-center lg:hidden">
                  <DashboardMenus />
                </div>

                <form
                  onSubmit={handleSearchSubmit}
                  className="relative hidden w-full min-w-[280px] sm:block"
                >
                  <FiSearch
                    className="pointer-events-none absolute start-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400"
                    aria-hidden="true"
                  />
                  <input
                    ref={searchRef}
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder={searchPlaceholder}
                    dir={isRtl ? "rtl" : "ltr"}
                    className="h-11 w-full rounded-full border border-slate-200 bg-white ps-11 pe-16 text-start text-sm text-slate-700 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15"
                    aria-label={searchPlaceholder}
                  />
                  <span
                    dir="ltr"
                    className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-400"
                  >
                    ⌘K
                  </span>
                </form>
              </div>

              <div
                className={`${
                  desktopShell ? "flex" : "hidden lg:flex"
                } shrink-0 items-center gap-2 ${isRtl ? "flex-row-reverse" : ""}`}
              >
                {isSuperAdmin && <OrganizationContextSelector />}

                {!isSuperAdmin && (
                  <span className="rounded-full bg-[#e8f8f3] px-3 py-1.5 text-xs font-semibold text-[#0f766e]">
                    {orgBadgeLabel}
                  </span>
                )}

                <div className="relative z-[110]" ref={notificationsRef}>
                  <button
                    type="button"
                    className={`${pillButtonClass} relative h-10 w-10`}
                    onClick={handleToggleNotificationsMenu}
                    aria-label="Notifications"
                    aria-expanded={showNotificationsMenu}
                  >
                    <FiBell className="h-[18px] w-[18px]" />
                    {notificationBadgeCount > 0 && (
                      <span className="absolute -end-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#EF4A00] px-1 text-[10px] font-bold text-white">
                        {notificationBadgeCount > 99 ? "99+" : notificationBadgeCount}
                      </span>
                    )}
                  </button>

                  {showNotificationsMenu && canAccessCcNotificationMenu() && (
                    <div className="absolute end-0 z-[110] mt-2 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 shadow-xl">
                      {renderNotificationMenuItems(
                        "flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
                  <LangComp toggleLangMode={toggleLangMode} isNavbarOpen={isNavbarOpen} />
                  <IoChevronDownOutline
                    className="h-3.5 w-3.5 shrink-0 text-slate-400"
                    aria-hidden="true"
                  />
                </div>

                <div className="relative z-[110]" ref={profileRef} data-testid="profile-avatar">
                  <button
                    type="button"
                    onClick={openProfileMenu}
                    className="flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm transition hover:border-slate-300"
                  >
                    <span className="text-start">
                      <span className="block max-w-[140px] truncate text-sm font-semibold text-slate-900">
                        {user}
                      </span>
                      <span className="block text-xs text-slate-400">
                        {formatUserType(userType)}
                      </span>
                    </span>
                  </button>

                  {showProfileModal && (
                    <div
                      data-testid="profile-menu"
                      className="absolute end-0 z-[110] mt-2 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 shadow-xl"
                    >
                      <div className="mx-2 mb-2 flex items-center gap-3 rounded-xl bg-slate-50 px-3 py-3">
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={profilePic}
                          alt="Avatar"
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-800">{user}</p>
                          <p className="text-xs text-slate-500">{formatUserType(userType)}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="profile-menu-my-profile"
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                        onClick={() => {
                          navigate("/user-profile");
                          setShowProfileModal(false);
                        }}
                      >
                        <FiUser className="h-4 w-4 text-slate-400" />
                        {translations[langMode].myprofile}
                      </button>
                      <button
                        type="button"
                        data-testid="profile-menu-logout"
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                        onClick={handleLogout}
                      >
                        <FiLogOut className="h-4 w-4 text-slate-400" />
                        {translations[langMode].Logout}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div
                className={`inline-flex shrink-0 items-center gap-2 lg:hidden ${
                  isRtl ? "flex-row-reverse" : ""
                }`}
              >
                <button
                  type="button"
                  className={`${pillButtonClass} relative h-10 w-10`}
                  onClick={handleToggleNotificationsMenu}
                  aria-label="Notifications"
                  aria-expanded={showNotificationsMenu}
                >
                  <FiBell className="h-[18px] w-[18px]" />
                  {notificationBadgeCount > 0 && (
                    <span className="absolute -end-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#EF4A00] px-1 text-[10px] font-bold text-white">
                      {notificationBadgeCount > 99 ? "99+" : notificationBadgeCount}
                    </span>
                  )}
                </button>
                <Disclosure.Button
                  onClick={() => setIsNavbarOpen(!isNavbarOpen)}
                  className={`${pillButtonClass} h-10 w-10`}
                >
                  <span className="sr-only">Open main menu</span>
                  {isNavbarOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
                </Disclosure.Button>
              </div>
            </div>

            <Disclosure.Panel static>
              {isNavbarOpen && (
                <div className="lg:hidden">
                  <button
                    type="button"
                    className="fixed inset-0 z-40 bg-slate-900/40"
                    onClick={() => setIsNavbarOpen(false)}
                    aria-label="Close account menu"
                  />
                  <div className="absolute end-3 top-[4.5rem] z-50 w-72 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
                    <div className="bg-slate-50 px-4 py-4">
                      <p className="text-sm font-semibold text-slate-800">{user}</p>
                      <p className="text-xs text-slate-500">{formatUserType(userType)}</p>
                      {desktopShell && isSuperAdmin && (
                        <div className="mt-3">
                          <OrganizationContextSelector />
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      {canAccessCcNotificationMenu() ? (
                        renderNotificationMenuItems(
                          "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm text-slate-700 hover:bg-slate-50"
                        )
                      ) : (
                        <button
                          type="button"
                          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm text-slate-700 hover:bg-slate-50"
                          onClick={handleToggleNotificationsMenu}
                        >
                          <FiBell className="h-4 w-4 text-slate-400" />
                          <span>
                            Notifications
                            <span className="ms-2 text-xs text-slate-400">{totalUnreadMsgCount} unread</span>
                          </span>
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid="logout"
                        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm text-slate-700 hover:bg-slate-50"
                        onClick={handleLogout}
                      >
                        <FiLogOut className="h-4 w-4 text-slate-400" />
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
      <Logout setShowModal={setShowModal} showModal={showModal} isNavbarOpen={isNavbarOpen} />
    </>
  );
};
